import { useApolloClient } from "@apollo/client"
import { Button } from "@chakra-ui/react"
import { slugifyItem } from "@pathwright/utils/src/text/slug"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Link from "@pathwright/ux/src/components/link/Link"
import { useEffect, useMemo } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import {
  CoverPageOfferFragment,
  HomeRegistrationsDocument,
  RegistrationRoleEnum,
  useCreateCurriculumLicenseOrderMutation,
  useResourceLicenseQuery
} from "@pathwright/graphql-client/src/generated"
import { usePollForOrderFulfillment } from "../order/useOrderPollingQuery"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getGroupUrl } from "../utils/urls"
import { OfferButtonRenderProps } from "./CoverPageOffer"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageTranslate } from "./utils"

// For licensing a path from another school (via store flow).
// On the licensor school, open /sign-up/?license=… in a new tab.
// On a connected licensee space, license in place via createCurriculumLicenseOrder.
// NOTE: in-place licensing assumes the offer can be used without payment or
// further input. Paid license checkout is still to do.
function CoverPagePathOfferButton({
  offer,
  renderButton,
  ...buttonProps
}: {
  offer: CoverPageOfferFragment
  renderButton?: (props: OfferButtonRenderProps) => React.ReactNode
} & OfferButtonRenderProps) {
  const client = useApolloClient()
  const { t } = useCoverPageTranslate()
  const { school } = usePathwrightContext()
  const paintResource = useCoverPageContext((v) => v.paintResource)
  const canPlaceOrder = school.id !== offer.school_id
  const signupHref = offer.resource_license_offering_id
    ? `/sign-up/?license=${offer.resource_license_offering_id}`
    : "/sign-up/"

  const newPathName = paintResource?.name || ""

  // 1. Create the order to license the new path.
  const [orderMutation, orderMutationState] =
    useCreateCurriculumLicenseOrderMutation({
      variables: {
        name: newPathName,
        license_offering_id: offer.resource_license_offering_id!,
        licensor_school_id: offer.school_id,
        licensee_school_id: school.id,
        role: RegistrationRoleEnum.Teacher
      }
    })

  const orderId =
    orderMutationState.data?.createCurriculumLicenseOrder?.id ?? null

  // 2. Poll for the order to be fulfilled.
  const [orderFulfilled, orderQuery] = usePollForOrderFulfillment(orderId, {
    pollInterval: 1000
  })
  const order = orderQuery.data?.order

  useEffect(() => {
    if (orderFulfilled) {
      client.refetchQueries({ include: [HomeRegistrationsDocument] })
    }
  }, [orderFulfilled])

  // 3. Query the new resource and cohort that were created by the order.
  const resourceQuery = useResourceLicenseQuery({
    variables: {
      id: order?.fulfilled_object_id!
    },
    skip: !order || !order.fulfilled_object_id
  })
  const newCohort = resourceQuery.data?.resource_license?.group
  const newResource = newCohort?.resource

  const loading = orderMutationState.loading || (!!orderId && !newResource)

  const newResourceLink = useMemo(() => {
    if (newCohort && newResource) {
      return getGroupUrl(slugifyItem(newResource), newCohort.id)
    }
    return undefined
  }, [newCohort, newResource])

  useEffect(() => {
    if (newResource && newResourceLink) {
      // 4. Show a success toast with a link to the new path.
      toaster.create({
        title: t("path_offer.added_to_learning_space", {
          defaultValue: "{{name}} added to your Learning Space",
          name: newResource.name
        }),
        description: (
          <DarkMode>
            <Link to={newResourceLink}>
              {t("toolbar.view_path", { defaultValue: "View Path" })}
            </Link>
          </DarkMode>
        ),
        type: "success",
        duration: 6000,
        closable: true
      })
    }
  }, [newResource, newResourceLink])

  const passedButtonProps: OfferButtonRenderProps = canPlaceOrder
    ? newResourceLink
      ? {
          ...buttonProps,
          children: t("toolbar.view_path", { defaultValue: "View Path" }),
          // Remove the to and as props to prevent the button from being a link.
          to: "/home/",
          as: ReactRouterLink
        }
      : {
          ...buttonProps,
          loading,
          loadingText: t("path_offer.copying_path", {
            defaultValue: "Copying Path..."
          }),
          disabled: loading,
          onClick: newResourceLink ? undefined : () => orderMutation(),
          // Remove the to and as props to prevent the button from being a link.
          to: undefined,
          as: undefined
        }
    : {
        ...buttonProps,
        as: "a",
        href: signupHref,
        target: "_blank",
        rel: "noopener noreferrer",
        to: undefined,
        relativeTo: undefined,
        onClick: (e: React.MouseEvent) => {
          // Prevent the card wrapper from opening the offer immersive route.
          e.stopPropagation()
        }
      }

  return renderButton ? (
    renderButton(passedButtonProps)
  ) : (
    <Button {...passedButtonProps} />
  )
}

export default CoverPagePathOfferButton
